import React from 'react'
import { observer } from 'mobx-react'
import { observe } from 'mobx'
import * as moment from 'moment-timezone'
import {
    AndroidFilled,
    AppleFilled,
    ChromeFilled,
    ClockCircleFilled,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    RetweetOutlined,
    ReloadOutlined,
    TrophyFilled,
    TagFilled,
} from '@ant-design/icons'
import { AppService, NotificationService } from '../../../services'
import { AppState } from '../../../stores/app'
import { Container } from 'typescript-ioc/es5'
import { DomainDto } from '../../../dtos/domain'
import { StatusType } from '../../../enums/status-type'
import { arrayContains, backfillStats, numberWithCommas, preventBubbling, titleCase } from '../../../_utils/utils'
import {
    BASE_TIME_FORMAT,
    BASE_TIME_FORMAT_WITHOUT_TZ,
    DEFAULT_HIGHCHARTS_CONFIG,
    HIGHCHARTS_CHANNEL_COLOR_MAP,
    FEAT_ENABLE_NOTIFICATION_DISPLAY_BEHAVIOR,
} from '../../../constants'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { NotificationSummaryCard, StatusBadge, PushlyAi, Well, PageHeader } from '@pushly/aqe/lib/components'
import {
    DetailsSummaryWell,
    DetailsSummarySection,
    DetailsSummarySubSections,
    DetailsSummaryField,
} from '@pushly/aqe/lib/components/details-summary-well'
import { BaseNotificationDetails, IBaseNotificationDetailsState } from './base-notification-details-component'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'
import { GetSegmentResponse } from '@pushly/models/lib/structs/segmentation'
import { isTzDelivery, isNotificationEditable } from '../helpers/notification-helpers'
import { NOTIFICATION_FIELD_DESCRIPTIONS } from '../field-descriptions'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { SubjectEntity } from '../../../enums/ability-entity.enum'
import { asCaslSubject } from '../../../stores/app-ability'
import { EditOutlined } from '@ant-design/icons'
import { InsightsService } from '../../../services/insights'
import { NotificationDeliveryWindow } from '@pushly/models/lib/enums/notification-delivery-window'
import { NotificationDeliveryType } from '@pushly/models/lib/enums/notification-delivery-type'
import { extractSummaryNotification, extractAiFields } from '../../../_utils/notifications'
import { getCollapseKeyDisplayLabel } from '@pushly/aqe/lib/utils/notification-collapse-key'
import { ApiVersion } from '../../../enums/api-version.enum'
import { NoTranslate } from '../../../components/no-translate/no-translate'
import { SwLink } from '../../../components/sw-link/sw-link'
import { NotificationStatusBadge } from '../../../components/badges/notification-status-badge'
import { HighchartsUtils } from '../../../_utils/highcharts-utils'
import { onResponseError403 } from '../../../_utils/on-response-error-403'
import { reduceStatTotalsByChannel } from './helpers'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { NotificationSource } from '../../../enums/notification-source'
import { NotificationPreviewState } from '../../../stores/notification-preview-state'
import { coalesce } from '../../../_utils/coalesce'
import { getDomainCurrencySymbol } from '../../../_utils/domain'
import { ReportEntity } from '../../reporting_v2/enums/report-entity'
import { ReportDatePreset } from '../../reporting_v2/enums/report-date-preset'
import { ReportDateIncrement } from '../../reporting_v2/enums/report-date-increment'
import { InsightsReportContract } from '../../reporting_v2/helpers/build-api-report-contract'
import { PreviewPlatform } from '@pushly/models/lib/enums/preview-platform'
import { PreviewState } from '@pushly/models/lib/enums/preview-state'
import { Button, Col, Divider, Popover, Row, Select, Space, Tag, Tooltip, Flex } from 'antd'
import { StatsWell, StatItem } from '../../../components/details-view/stats-well/'
import './notification-details.scss'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'
import { INotificationFieldSuggestions } from '../../../interfaces/notification-field-suggestions'

Highcharts.setOptions({ lang: { thousandsSep: ',' } })

type ChartType = 'impressions' | 'clicks'

interface IHeaderProps {
    domain: DomainDto
    notification?: GetNotificationResponse
}

export class NotificationDetailsHeader extends React.Component<IHeaderProps, {}> {
    protected appState: AppState
    protected appService: AppService

    private unmounting: boolean = false

    public constructor(props: IHeaderProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public componentWillUnmount() {
        this.unmounting = true
    }

    public render(): React.ReactNode {
        const loading = !this.notif
        const isCampaign = this.notif?.source === NotificationSource.CAMPAIGN

        return (
            <div className="details-header">
                <div className="notif-title">
                    <PageHeader
                        title={<NoTranslate>{this.notif?.template?.channels?.default?.title}</NoTranslate>}
                        append={
                            <div className="notif-id">
                                <span>
                                    #{this.notif?.id}
                                    <span>
                                        {' '}
                                        | Created by <NoTranslate>{this.notif?.created_by_user_name}</NoTranslate>&nbsp;
                                    </span>
                                </span>
                            </div>
                        }
                        action={this.renderEditAction()}
                        onTitleSet={this.appService.customizeTabTitle}
                    />
                </div>

                <DetailsSummaryWell loading={loading} collapsible={true}>
                    <DetailsSummarySection title="General">
                        <DetailsSummaryField label="ID" value={this.notif?.id} copyable />
                        <DetailsSummaryField
                            label="Status"
                            info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.status, inline: true }}
                            value={
                                this.notif && (
                                    <NotificationStatusBadge
                                        status={this.notif.status!}
                                        expanded={true}
                                        holdOut={
                                            this.notif.delivery_spec?.window === NotificationDeliveryWindow.HOLD_OUT
                                        }
                                    />
                                )
                            }
                        />
                        <DetailsSummaryField
                            label="Channels"
                            value={this.renderChannelValues()}
                            info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.channels, inline: true }}
                        />
                        {this.renderNotificationTestField()}
                    </DetailsSummarySection>

                    <DetailsSummarySubSections>
                        <DetailsSummarySection title="Delivery">
                            {!isCampaign && (
                                <DetailsSummaryField
                                    label="Delivery Date Range"
                                    value={this.notif ? this.buildFlightDatesValue() : undefined}
                                    info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.deliveryDateRange, inline: true }}
                                />
                            )}
                            <DetailsSummaryField
                                label="Delivery Strategy"
                                value={this.notif ? this.buildStrategyValue() : undefined}
                                info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.deliveryStrategy, inline: true }}
                            />
                            {this.notif && this.renderReplacementBehaviorField()}
                            {this.notif && this.renderBehaviorsField()}
                        </DetailsSummarySection>

                        {!isCampaign && (
                            <DetailsSummarySection title="Targeting">
                                {this.notif && this.renderSegmentFields()}
                            </DetailsSummarySection>
                        )}
                    </DetailsSummarySubSections>

                    {this.notif && this.renderContentSection()}
                </DetailsSummaryWell>
            </div>
        )
    }

    protected get notif(): GetNotificationResponse {
        return this.props.notification!
    }

    protected renderEditAction(): React.ReactNode {
        if (!this.notif || !isNotificationEditable(this.notif)) {
            return undefined
        }

        const canUpdate = this.appState.abilityStore.can(
            AbilityAction.UPDATE,
            asCaslSubject(SubjectEntity.NOTIFICATION, { domainId: this.props.domain?.id }),
        )
        if (!canUpdate) {
            return undefined
        }

        const editUrl = this.appService.routeWithinDomain(`/notifications/edit/${this.notif.id}`, true)

        return (
            <Button
                type="primary"
                icon={<EditOutlined />}
                href={editUrl}
                onClick={(ev) => {
                    if (!ev.metaKey) {
                        ev.preventDefault()
                        this.appService.route(editUrl)
                    }
                }}
            >
                Edit
            </Button>
        )
    }

    protected buildFlightDatesValue(): React.ReactNode {
        const isSTZ = isTzDelivery(this.notif)
        const sendDateFormat = isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT

        const startDate = moment.tz(this.notif.delivery_spec?.send_date_utc, this.props.domain.timezone)
        const expiryDate = startDate.clone().add(this.notif.delivery_spec?.ttl_seconds, 'seconds')

        const flightDateStart = startDate.format(sendDateFormat)
        const flightDateEnd = expiryDate.format(sendDateFormat)

        return (
            <>
                {flightDateStart} <span style={{ opacity: 0.5 }}>through</span>
                <br />
                {flightDateEnd}
            </>
        )
    }

    protected buildStrategyValue(): string {
        const type = this.notif.delivery_spec?.type
        const window = this.notif.delivery_spec?.window
        let strategyStr = ''

        if (this.notif.source === NotificationSource.CAMPAIGN) {
            strategyStr = 'journey schedule'
        } else if (window === NotificationDeliveryWindow.HOLD_OUT) {
            strategyStr = 'hold out'
        } else if (window === NotificationDeliveryWindow.INFORMED) {
            strategyStr = 'informed'
        } else if (type === NotificationDeliveryType.IMMEDIATE) {
            strategyStr = 'immediate'
        } else if (window === NotificationDeliveryWindow.STANDARD) {
            strategyStr = 'fixed'
        } else if (window === NotificationDeliveryWindow.TIMEZONE) {
            strategyStr = 'subscriber time zone'
        }

        return titleCase(strategyStr)
    }

    protected renderContentSection(): React.ReactNode {
        const aiResults = this.notif?.template?.channels?.default?.auto_suggested_fields_results as any

        // Backward compatibility: Legacy notifications created before the source field was added
        // may have AI-generated content (title, body, imageUrl, keywords) but no source field.
        // In this case, default to 'page_analyzer' since it was the only AI tool available at that time.
        let aiSource = aiResults?.source
        if (
            aiResults &&
            !aiSource &&
            (aiResults.title || aiResults.body || aiResults.imageUrl || aiResults.keywords?.length)
        ) {
            aiSource = 'page_analyzer'
        }

        if (!aiSource) return null

        const isPushlyAi = aiSource === 'pushly_ai'
        const tone = aiResults?.source_metadata?.tone ?? aiResults?.sourceMetadata?.tone ?? aiResults?.tone
        const reason = aiResults?.source_metadata?.reason ?? aiResults?.sourceMetadata?.reason ?? aiResults?.reason

        return (
            <DetailsSummarySection title="Content">
                <DetailsSummaryField
                    label="Content Source"
                    info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.contentSources, inline: true }}
                    value={
                        <>
                            {isPushlyAi && <PushlyAi />} {isPushlyAi ? 'Pushly AI' : 'Page Analyzer'}
                        </>
                    }
                />
                {tone && <DetailsSummaryField label="AI Content Tone" value={<Tag>{titleCase(tone)}</Tag>} />}
                {reason && <DetailsSummaryField label="AI Content Reason" value={reason} />}
            </DetailsSummarySection>
        )
    }

    protected renderSegmentFields(): React.ReactNode {
        let subscriberTags: React.ReactNode
        let excludedSubscribers: React.ReactNode
        const allSubscriberTag: React.ReactNode = (
            <a key="all-subs" className="no-click">
                <Tag>All Subscribers</Tag>
            </a>
        )
        if (this.notif.audience?.segments) {
            let tags: React.ReactNode[] = [allSubscriberTag]
            const isAllSubscribers = this.notif.audience.segments.find((s) => s.is_default)

            if (!isAllSubscribers) {
                tags = this.notif.audience.segments
                    .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
                    .map((segment, idx: number) => (
                        <a
                            key={`i-${idx}`}
                            onClick={this.jumpToSegment.bind(this, segment)}
                            href={this.buildSegmentUrl(segment)}
                        >
                            <Tag>{segment.name}</Tag>
                        </a>
                    ))
            }

            subscriberTags = <>{tags}</>
        } else if (this.notif.audience?.external_subscriber_ids) {
            subscriberTags = <Tag>Subscriber IDs</Tag>
        }

        if (this.notif.audience?.excluded_segments && this.notif.audience.excluded_segments.length > 0) {
            excludedSubscribers = (
                <>
                    {this.notif.audience.excluded_segments
                        .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
                        .map((segment, idx: number) => (
                            <a
                                key={`x-${idx}`}
                                onClick={this.jumpToSegment.bind(this, segment)}
                                href={this.buildSegmentUrl(segment)}
                            >
                                <Tag>{segment.name}</Tag>
                            </a>
                        ))}
                </>
            )
        }

        return (
            <>
                <DetailsSummaryField
                    label="Targeted Audience(s)"
                    value={subscriberTags}
                    info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.targetedAudiences, inline: true }}
                />
                {!!excludedSubscribers && (
                    <DetailsSummaryField
                        label="Excluded Audience(s)"
                        value={excludedSubscribers}
                        info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.excludedAudiences, inline: true }}
                    />
                )}
            </>
        )
    }

    protected renderChannelValues(): React.ReactNode {
        const channels = this.notif?.channels
        if (!channels) return undefined

        const items: React.ReactNode[] = []
        if (channels.includes(DeliveryChannel.WEB))
            items.push(
                <span key="web">
                    <ChromeFilled /> Web
                </span>,
            )
        if (channels.includes(DeliveryChannel.NATIVE_IOS))
            items.push(
                <span key="ios">
                    <AppleFilled /> Native Apple
                </span>,
            )
        if (channels.includes(DeliveryChannel.NATIVE_ANDROID))
            items.push(
                <span key="android">
                    <AndroidFilled /> Native Android
                </span>,
            )

        if (items.length === 0) return undefined

        return items.reduce<React.ReactNode[]>((acc, item, i) => {
            if (i > 0) acc.push(', ')
            acc.push(item)
            return acc
        }, [])
    }

    protected renderNotificationTestField(): React.ReactNode {
        if (!this.notif?.ab_test) return null

        const testUrl = this.buildAbTestUrl(this.notif)
        const isWinner = !!this.notif.ab_test.winner

        return (
            <DetailsSummaryField
                label="Notification Test"
                value={
                    <>
                        <SwLink href={testUrl}>{this.notif.ab_test.name}</SwLink>
                        {isWinner && (
                            <>
                                {' '}
                                <TrophyFilled className="test-winner-icon" /> Winner
                            </>
                        )}
                    </>
                }
            />
        )
    }

    protected renderBehaviorsField(): React.ReactNode {
        const fields: React.ReactNode[] = []

        if (this.notif?.template?.channels?.default?.is_silent) {
            fields.push(
                <DetailsSummaryField
                    key="silent-delivery"
                    label="Silent Delivery"
                    value="Enabled"
                    info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.silentDelivery, inline: true }}
                />,
            )
        }
        if (this.notif?.template?.channels?.default?.require_interaction) {
            fields.push(
                <DetailsSummaryField
                    key="requires-interaction"
                    label="Requires Interaction"
                    value="Enabled"
                    info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.requiresInteraction, inline: true }}
                />,
            )
        }

        if (fields.length === 0) return null

        return <>{fields}</>
    }

    protected renderReplacementBehaviorField(): React.ReactNode {
        if (!this.notif) return null
        if (!this.props.domain?.flags?.includes(FEAT_ENABLE_NOTIFICATION_DISPLAY_BEHAVIOR)) return null

        const { label, replacementGroupKey } = getCollapseKeyDisplayLabel(
            this.notif.collapse_key,
            this.props.domain?.collapseKeyType,
            { domainId: this.notif.domain_id, notificationId: this.notif.id },
        )

        const displayValue = replacementGroupKey ? `${label}: ${replacementGroupKey}` : label

        return (
            <DetailsSummaryField
                label="Replacement Behavior"
                value={displayValue}
                info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.replacementBehavior, inline: true }}
            />
        )
    }

    private buildSegmentUrl(segment: GetSegmentResponse): string {
        return this.appService.routeWithinDomain(`/segments/${segment.id}/summary`, true)
    }

    private buildAbTestUrl(notif: GetNotificationResponse): string {
        return this.appService.routeWithinDomain(`/notifications/test/${notif.ab_test!.id}/summary`, true)
    }

    private jumpToSegment = (segment: GetSegmentResponse, ev?: React.MouseEvent): void => {
        if (!ev?.metaKey) {
            preventBubbling(ev)
            this.appService.route(this.buildSegmentUrl(segment))
        }
    }
}

interface IProps {}

interface IState extends IBaseNotificationDetailsState {
    loadingSchedule: boolean
    loadingGenStats: boolean
    loadingBreakdownStats: boolean
    loadingActionStats: boolean
    domain: DomainDto
    notification?: GetNotificationResponse
    stats?: any
    actionStats?: any[]
    breakdownStats?: any
    statsType: ReportDateIncrement.DAY | ReportDateIncrement.LIFETIME
    performanceMetric: ChartType
    performanceChannel: DeliveryChannel[]
    platform: PreviewPlatform
}

@observer
export class NotificationDetails extends BaseNotificationDetails<IProps, IState> {
    protected appService: AppService
    protected readonly notificationPreviewState: NotificationPreviewState
    private appState: AppState
    private notificationService: NotificationService
    private insightsService: InsightsService

    private disposeObservers: (() => void)[]

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.notificationService = Container.get(NotificationService)
        this.insightsService = Container.get(InsightsService)
        this.notificationPreviewState = Container.get(NotificationPreviewState)

        this.state = {
            loadingSchedule: true,
            loadingGenStats: true,
            loadingBreakdownStats: true,
            loadingActionStats: true,
            domain: this.appState.currentDomain!,
            statsType: ReportDateIncrement.DAY,
            tabs: [],
            performanceMetric: 'impressions',
            performanceChannel: [],
            platform: PreviewPlatform.ANDROID,
        }
    }

    public async componentDidMount() {
        this.disposeObservers = [observe(this.appState, 'currentDomainJsonData', () => this.setDomainState())]

        await this.setDomainState()

        const tabs = [
            {
                name: 'summary',
                label: 'Summary',
                render: this.renderSummaryTab,
            },
        ]

        const activeTab = this.determineActiveTab(tabs)
        if (!this.unmounting) {
            await this.setState({
                tabs,
                activeTab,
            })
        }
    }

    public async componentDidUpdate(prevProps, prevState, _) {
        if (prevState.statsType !== this.state.statsType) {
            await this.refreshStats()
        }
    }

    public componentWillUnmount() {
        super.componentWillUnmount()
        this.disposeObservers.forEach((f: any) => f())
    }

    public render(): React.ReactNode {
        let optionsWrapper: React.ReactNode
        let subscribers: React.ReactNode
        let excludedSubscribers: React.ReactNode
        const allSubscriberTag: React.ReactNode = (
            <a key="all-subs">
                <Tag variant="outlined" color="gray">
                    All Subscribers
                </Tag>
            </a>
        )

        let dateAppend: React.ReactNode = ''

        if (this.state.notification) {
            const sample = this.state.notification
            const sampleAudience = sample.audience

            const now = moment()
            const sendDate = moment.tz(sample.delivery_spec?.send_date_utc, this.localDomain!.timezone)
            sendDate.add(sample.delivery_spec?.ttl_seconds, 'seconds')

            const isSTZ = isTzDelivery(sample)
            const sendDateFormat = isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT

            dateAppend = (
                <span key="expiry">
                    {sendDate > now ? ' | Expires ' : ' | Expired '} {'on ' + sendDate.format(sendDateFormat)}
                </span>
            )

            if (sample.source !== NotificationSource.CAMPAIGN) {
                if (sampleAudience?.segments) {
                    let subscriberTags: React.ReactNode[] = [allSubscriberTag]
                    const isAllSubscribers = sampleAudience.segments.find((s: any) => s.is_default)

                    if (!isAllSubscribers) {
                        subscriberTags = sampleAudience.segments.map((segment: any, idx: number) => (
                            <a
                                key={`i-${idx}`}
                                onClick={this.jumpToSegment.bind(this, segment)}
                                href={this.buildSegmentUrl(segment)}
                            >
                                <Tag variant="outlined" color="gray">
                                    {segment.name}
                                </Tag>
                            </a>
                        ))
                    }

                    subscribers = (
                        <div className="included-segments-header">
                            <span className="heading">Included Segments:</span>
                            <span className="segments">{subscriberTags}</span>
                        </div>
                    )
                } else if (sampleAudience?.external_subscriber_ids) {
                    subscribers = (
                        <Tag variant="outlined" color="gray">
                            Subscriber IDs
                        </Tag>
                    )
                }

                if (sampleAudience?.excluded_segments && sampleAudience.excluded_segments.length > 0) {
                    excludedSubscribers = (
                        <div className="excluded-segments-header">
                            <span className="heading">Excluded Segments:</span>
                            <span className="segments">
                                {sampleAudience.excluded_segments.map((segment: any, idx: number) => (
                                    <a
                                        key={`x-${idx}`}
                                        onClick={this.jumpToSegment.bind(this, segment)}
                                        href={this.buildSegmentUrl(segment)}
                                    >
                                        <Tag variant="outlined" color="gray">
                                            {segment.name}
                                        </Tag>
                                    </a>
                                ))}
                            </span>
                        </div>
                    )
                }
            }

            const optionsArray: any[] = []
            if (this.state.notification.template?.channels?.default?.is_silent) {
                optionsArray.push(
                    <span key="silent" className="option">
                        <Tooltip title="Silent">
                            <i className="icon-muted" />
                        </Tooltip>
                    </span>,
                )
            }
            if (isTzDelivery(this.state.notification)) {
                optionsArray.push(
                    <span key="stz" className="option">
                        <Tooltip title="Delivered in Subscriber's Time Zone">
                            <ClockCircleFilled />
                        </Tooltip>
                    </span>,
                )
            }
            if (optionsArray.length > 0) {
                optionsWrapper = (
                    <div className="options-header">
                        <span className="heading">Options:</span>
                        <span className="options">{optionsArray}</span>
                    </div>
                )
            }
        }

        return (
            <Flex vertical gap={VERTICAL_WELL_GAP} className="notification-details page">
                <NotificationDetailsHeader
                    domain={this.appState.currentDomain!}
                    notification={this.state.notification}
                />

                {!this.state.loadingSchedule && this.renderTabs()}
            </Flex>
        )
    }

    public renderSummaryTab(): React.ReactNode {
        const summaryNotification = extractSummaryNotification(this.state.notification!, this.state.platform)

        // default content
        let title = summaryNotification.title
        let body = summaryNotification.body
        let previewIcon = summaryNotification.icon
        let previewLandingUrl = summaryNotification.landingUrl
        let subtitle
        let actions = summaryNotification.actions

        const defaultTemplate = this.state.notification?.template?.channels?.default
        const autoSuggestedFieldsResults = defaultTemplate?.auto_suggested_fields_results as
            | INotificationFieldSuggestions
            | null
            | undefined

        if (['mac_os', 'windows', 'android'].includes(this.state.platform)) {
            title = this.state.notification?.template?.channels?.web?.title ?? summaryNotification.title
            previewLandingUrl =
                this.state.notification?.template?.channels?.web?.landing_url ?? summaryNotification.landingUrl
            body = this.state.notification?.template?.channels?.web?.body ?? summaryNotification.body
            previewIcon = this.state.notification?.template?.channels?.web?.icon_url ?? summaryNotification.icon
            actions = this.state.notification?.template?.channels?.web?.actions?.length
                ? (this.state.notification?.template?.channels?.web?.actions as any)
                : summaryNotification.actions
        }

        if (this.state.platform === 'ios') {
            title = this.state.notification?.template?.channels?.native_ios?.title ?? summaryNotification.title
            previewLandingUrl =
                this.state.notification?.template?.channels?.native_ios?.landing_url ?? summaryNotification.landingUrl
            body = this.state.notification?.template?.channels?.native_ios?.body ?? summaryNotification.body
            previewIcon = this.state.notification?.template?.channels?.native_ios?.icon_url ?? summaryNotification.icon
            subtitle = this.state.notification?.template?.channels?.native_ios?.subtitle
        }

        if (this.state.platform === 'native_android') {
            title = this.state.notification?.template?.channels?.native_android?.title ?? summaryNotification.title
            previewLandingUrl =
                this.state.notification?.template?.channels?.native_android?.landing_url ??
                summaryNotification.landingUrl
            body = this.state.notification?.template?.channels?.native_android?.body ?? summaryNotification.body
            previewIcon =
                this.state.notification?.template?.channels?.native_android?.icon_url ?? summaryNotification.icon
            actions = this.state.notification?.template?.channels?.native_android?.actions?.length
                ? (this.state.notification?.template?.channels?.native_android?.actions as any)
                : summaryNotification.actions
        }

        const previewTitle = <NoTranslate>{title}</NoTranslate>
        const previewBody = body ? <NoTranslate>{body}</NoTranslate> : null
        const previewSubtitle = subtitle ? <NoTranslate>{subtitle}</NoTranslate> : null

        const nativeFcmConfig = this.state.domain.nativeFcmConfiguration as any

        const notificationShouldUseImageForIconWhenPresent = nativeFcmConfig.useImageForIconWhenPresent

        const nps = this.notificationPreviewState.getCache('domain', this.state.domain.id)
        const initialPlatform = nps.resolvePlatformFromAvailableChannels(this.state.notification?.channels)
        const previewState = coalesce(nps.state, PreviewState.EXPANDED)

        return (
            <React.Fragment>
                {this.hasPerformanceData && (
                    <div className="outer-well-buttons">
                        <Tooltip title="Refresh">
                            <Button
                                className="refresh-list"
                                type="text"
                                shape="round"
                                icon={
                                    <ReloadOutlined
                                        className="text--size--large"
                                        spin={this.state.loadingGenStats || this.state.loadingBreakdownStats}
                                    />
                                }
                                onClick={this.refreshStats}
                            />
                        </Tooltip>
                    </div>
                )}

                {this.hasPerformanceData ? this.renderStatsWell() : this.renderPerformanceEmptyState()}
                <br />

                <Row gutter={24} className="details-lower-row">
                    {this.hasPerformanceData && <Col span={14}>{this.renderBreakdownStats()}</Col>}
                    <Col span={this.hasPerformanceData ? 10 : 24}>
                        <NotificationSummaryCard
                            mode="dark"
                            loading={this.state.loadingGenStats}
                            domain={this.state.domain}
                            notification={
                                {
                                    ...summaryNotification,
                                    title: previewTitle,
                                    subtitle: previewSubtitle,
                                    body: previewBody,
                                    actions: actions.map((action) => ({
                                        ...action,
                                        label: <NoTranslate key={action.id}>{action.label}</NoTranslate>,
                                    })),
                                    icon: previewIcon,
                                    accentColor: nativeFcmConfig.notificationAccentColor,
                                    useImageForIconWhenPresent: notificationShouldUseImageForIconWhenPresent,
                                    landingUrl: previewLandingUrl,
                                    aiFields: extractAiFields(autoSuggestedFieldsResults),
                                } as any
                            }
                            initialPlatform={initialPlatform}
                            previewState={previewState}
                            onPlatformChange={(platform) =>
                                this.setState({
                                    platform,
                                })
                            }
                            onTitleClick={this.handleSummaryTitleClick}
                            hideStats={true}
                            channels={this.state.notification?.channels}
                            hideFooter={true}
                            userId={this.appState.currentUser?.id}
                        />
                    </Col>
                </Row>
            </React.Fragment>
        )
    }

    private get notificationIdParam(): string {
        return this.injectedProps.match.params.notificationId
    }

    private get isDelivered(): boolean {
        const { notification } = this.state
        const completedStatuses = [StatusType.COMPLETED.name, StatusType.COMPLETED_WITH_FAILURES.name]
        return (
            !!notification &&
            (arrayContains(completedStatuses, notification.status) ||
                this.isPartialFailure ||
                notification.source === NotificationSource.CAMPAIGN)
        )
    }

    private get isPartialFailure(): boolean {
        const { notification, stats } = this.state
        const hasStats = !!stats && stats.impressions > 0
        return !!notification && notification.status === StatusType.FAILED.name && hasStats
    }

    private renderBreakdownStats(): React.ReactNode {
        const channelOptions = this.state.notification?.channels ?? []

        return (
            <Well
                className="thin primary-stats-well"
                hideHeader={true}
                footerMode="hidden"
                loading={this.state.loadingBreakdownStats}
                disabled={this.isDisabled}
            >
                <Flex vertical>
                    <Flex justify="space-between" className="chart-header" align="flex-start">
                        <div className="chart-title">Performance Breakdown by Channel</div>
                        <Space className="chart-actions" align="start">
                            <Select
                                className="stats-type-select"
                                size="small"
                                value={this.state.statsType}
                                disabled={!this.state.breakdownStats?.length}
                                onChange={(val) => {
                                    this.setState({ statsType: val })
                                }}
                                options={[
                                    { label: 'Daily', value: ReportDateIncrement.DAY },
                                    { label: 'All Time', value: ReportDateIncrement.LIFETIME },
                                ]}
                            />
                            <div className="channel-selectors">
                                <DeliveryChannelSelector
                                    disabled={!this.state.breakdownStats?.length}
                                    disabledMessage="No stats are available for this Notification"
                                    type="multiple"
                                    nested={true}
                                    onChange={(change: DeliveryChannel[]) => {
                                        this.setState({ performanceChannel: change })
                                    }}
                                    value={this.state.performanceChannel}
                                    visibleChannels={channelOptions}
                                    allowEmptySelection={true}
                                />
                            </div>
                        </Space>
                    </Flex>

                    {!this.state.loadingBreakdownStats && this.renderBreakdownStatsChart()}
                </Flex>
            </Well>
        )
    }

    private renderPerformanceEmptyState(): React.ReactNode {
        const notification = this.state.notification!
        const isSTZ = isTzDelivery(notification)
        const sendDateFormat = isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT
        const sendDate = moment.tz(notification.delivery_spec?.send_date_utc, this.state.domain.timezone)

        return (
            <Well
                className="thin gen-stats-well nested"
                hideHeader={true}
                footerMode="hidden"
                bodyStyle={{
                    margin: 'auto',
                    padding: '24px 0',
                }}
            >
                <Space vertical className="performance-empty-state">
                    <ClockCircleOutlined className="empty-state-icon" aria-hidden="true" />
                    <span className="empty-state-title">Performance data will be available once delivery begins.</span>
                    <span className="empty-state-subtitle">Scheduled for {sendDate.format(sendDateFormat)}</span>
                </Space>
            </Well>
        )
    }

    private get isDisabled(): boolean {
        return !this.hasPerformanceData
    }

    /**
     * Returns true when the notification has reached a state where performance
     * stats are expected to exist (delivered, delivering informed/TZ push, or
     * cancelled informed/TZ push, or campaign-sourced).
     */
    private get hasPerformanceData(): boolean {
        const isDelivered = this.isDelivered
        const isDelivering = this.state.notification!.status === StatusType.DELIVERING.name
        const isCancelled =
            this.state.notification!.status === StatusType.CANCELLED.name ||
            this.state.notification!.status === StatusType.CANCELLING.name
        const isInf = this.state.notification!.delivery_spec?.window === NotificationDeliveryWindow.INFORMED
        const isStz = this.state.notification!.delivery_spec?.window === NotificationDeliveryWindow.TIMEZONE

        return isDelivered || ((isInf || isStz) && (isDelivering || isCancelled))
    }

    private renderBreakdownStatsChart = (): React.ReactNode => {
        const { stats, breakdownStats, notification } = this.state
        if (!stats) {
            return
        }
        if (!breakdownStats) {
            return ''
        }

        const xAxisDateDataSet = new Set(
            breakdownStats.map((stat) => moment.tz(stat.event_date, 'UTC').format('MMM D')),
        )

        const mapDataKeyFromObjectList = (key: string, objList: any[], channel: DeliveryChannel) => {
            return objList.filter((val) => val.channel === channel).map((filtered) => filtered[key] || 0)
        }

        const xAxisChannelDataSet = new Set(
            (notification?.channels ?? []).map((channel) => DeliveryChannel.getLongName(channel)),
        )

        const buildSeries = (chartType: ChartType) => {
            const series: any[] = [{ keys: ['name', 'y'], visible: false, showInLegend: false }]
            const colorType = chartType === 'impressions' ? 'primary' : 'secondary'

            if (this.state.statsType !== ReportDateIncrement.DAY) {
                series.push({
                    data: this.state.performanceChannel.map((channel) => {
                        const foundStat = breakdownStats.find((stat) => stat.channel === channel)
                        return {
                            name: DeliveryChannel.getLongName(channel),
                            color: HIGHCHARTS_CHANNEL_COLOR_MAP[channel][colorType],
                            y: foundStat?.[chartType] ?? 0,
                        }
                    }),
                })
            } else {
                series.push(
                    ...this.state.performanceChannel.map((channel) => ({
                        name: DeliveryChannel.getLongName(channel),
                        color: HIGHCHARTS_CHANNEL_COLOR_MAP[channel][colorType],
                        data: mapDataKeyFromObjectList(chartType, breakdownStats, channel),
                    })),
                )
            }

            return series
        }

        const options = (chartType: ChartType) => ({
            ...DEFAULT_HIGHCHARTS_CONFIG,
            swContext: (): IState => this.state,
            chart: {
                type: 'column',
                marginLeft: 70,
                marginRight: 50,
                spacingTop: 20,
                backgroundColor: 'transparent',
            },
            title: {
                text: titleCase(chartType),
            },
            xAxis: {
                categories:
                    this.state.statsType === ReportDateIncrement.DAY ? [...xAxisDateDataSet] : [...xAxisChannelDataSet],
            },
            legend: {
                enabled: false,
                itemStyle: { color: 'var(--color-black)' },
            },
            yAxis: [
                {
                    title: { text: '' },
                    allowDecimals: false,
                    startOnTick: 0,
                    minRange: 0,
                },
            ],
            tooltip: {
                pointFormatter: HighchartsUtils.commaTooltipPointFormatter,
            },
            // plotOpt
            series: buildSeries(chartType),
        })

        let impressionsTotals = [
            <div key="impressions" className="ant-popover-title">
                Impressions:
            </div>,
        ]
        let clicksTotals = [
            <div key="clicks" className="ant-popover-title">
                Clicks:
            </div>,
        ]
        const totalStatsByChannel = reduceStatTotalsByChannel(breakdownStats)

        Object.keys(totalStatsByChannel).map((key) => {
            impressionsTotals.push(
                <div key={`${key}-impressions`} className="channel-totals-row">
                    <span className="channel-totals-label">{DeliveryChannel.getShortName(DeliveryChannel[key])}:</span>
                    <span className="channel-totals-value">
                        {numberWithCommas(totalStatsByChannel[key].impressions)}
                    </span>
                </div>,
            )

            clicksTotals.push(
                <div key={`${key}-clicks`} className="reach-breakdown-row">
                    <span className="reach-breakdown-label">{DeliveryChannel.getShortName(DeliveryChannel[key])}:</span>
                    <span className="reach-breakdown-value">{numberWithCommas(totalStatsByChannel[key].clicks)}</span>
                </div>,
            )
        })

        return (
            <Popover
                key={`popover-details`}
                overlayClassName="domain-notif-channel-select-details-popover"
                content={
                    <div>
                        <div className="impressions-breakdown-stats">{impressionsTotals}</div>

                        <div className="clicks-breakdown-stats">{clicksTotals}</div>
                    </div>
                }
                placement="left"
            >
                <HighchartsReact
                    className="impressions-chart"
                    highcharts={Highcharts}
                    options={options('impressions')}
                />

                <Divider orientation="horizontal" />

                <HighchartsReact highcharts={Highcharts} options={options('clicks')} />
            </Popover>
        )
    }

    private renderStatsWell(): React.ReactNode {
        const stats = this.state.stats
        const actions: any = this.state.notification?.template?.channels?.default?.actions ?? []
        const hasActions = actions.length > 0
        const hasPurchases = (stats?.purchases ?? 0) > 0
        const hasPurchaseAmount = (stats?.purchase_amount ?? 0) > 0
        const currencySymbol = getDomainCurrencySymbol(this.appState.currentDomain)

        // Build action stat items using numeric type
        const actionItems: StatItem[] = []
        // Primary clicks
        const primaryClicks = stats?.primary_clicks ?? 0
        const primaryCtr = stats?.impressions ? primaryClicks / stats.impressions : 0
        actionItems.push({
            key: 'primary-clicks',
            type: 'numeric',
            label: 'Primary Clicks',
            value: primaryClicks,
            subValue: primaryCtr,
            subValueFormat: 'percent',
            tooltip: 'Primary Landing URL',
            animated: true,
        })

        if (hasActions && !this.isDisabled && !this.state.loadingActionStats) {
            // Button clicks
            actions.forEach((action: any, idx: number) => {
                const actionStat = this.state.actionStats?.find(
                    (s: any) => s.button.id.toString() === action.id.toString(),
                )
                actionItems.push({
                    key: `action-${action.id}`,
                    type: 'numeric',
                    label: `Button ${idx + 1} Clicks`,
                    value: actionStat?.clicks ?? 0,
                    subValue: actionStat?.ctr_decimal ?? 0,
                    subValueFormat: 'percent',
                    tooltip: action.label,
                    animated: true,
                })
            })
        }

        const items: StatItem[] = [
            {
                key: 'impressions',
                type: 'numeric',
                label: 'Impressions',
                value: stats?.impressions ?? 0,
                disabled: this.isDisabled,
                animated: true,
            },
            {
                key: 'clicks',
                type: 'numeric',
                label: `${hasActions ? 'Total ' : ''}Clicks (CTR)`,
                value: stats?.clicks ?? 0,
                subValue: stats?.ctr_decimal ?? 0,
                subValueFormat: 'percent',
                disabled: this.isDisabled,
                animated: true,
            },
            ...actionItems,
            {
                key: 'purchases',
                type: 'numeric',
                label: 'Purchases',
                value: stats?.purchases ?? 0,
                disabled: this.isDisabled,
                hidden: !hasPurchases,
                animated: true,
            },
            {
                key: 'purchase-amount',
                type: 'numeric',
                label: 'Purchase Amount',
                value: stats?.purchase_amount ?? 0,
                prefix: currencySymbol,
                precision: 2,
                disabled: this.isDisabled,
                hidden: !hasPurchaseAmount,
                animated: true,
            },
        ]

        return (
            <StatsWell loading={this.state.loadingGenStats} disabled={this.isDisabled} items={items} variant="cards" />
        )
    }

    private goBackToNotificationsList() {
        this.appService.routeWithinDomain('/notifications')
    }

    private async setDomainState(): Promise<void> {
        const { domain, notification } = this.state
        const selectedDomain = this.appState.currentDomain

        if (selectedDomain) {
            if (selectedDomain.id !== domain.id) {
                this.goBackToNotificationsList()
            } else {
                if (!this.unmounting) {
                    if (!domain) {
                        await this.setState({ domain: selectedDomain })
                    }

                    if (!notification) {
                        await this.ensureNotificationDetails(selectedDomain)
                    }
                }
            }
        }
    }

    private async ensureNotificationDetails(domain: DomainDto): Promise<void> {
        const notification = await this.notificationService.fetchNotificationById(domain.id, this.notificationIdParam, {
            query: { includeSegments: true },
            cancellationKey: 'nd.ensureNotificationDetails',
            version: ApiVersion.V4,
            errorHandler: onResponseError403(() => {
                this.goBackToNotificationsList()
            }),
        })

        const channelPlatform = {
            [DeliveryChannel.WEB]: PreviewPlatform.WINDOWS,
            [DeliveryChannel.NATIVE_IOS]: PreviewPlatform.IOS,
            [DeliveryChannel.NATIVE_ANDROID]: PreviewPlatform.ANDROID,
        }
        if (!this.unmounting && notification) {
            const channels = notification.channels

            await this.setState({
                loadingSchedule: false,
                notification,
                platform: channelPlatform[channels[0]],
            })

            // Only fetch stats when the notification has reached a delivery state
            if (this.hasPerformanceData) {
                this.getNotificationGenStats()
                this.getNotificationBreakdownStats()
            } else {
                this.setState({ loadingGenStats: false, loadingBreakdownStats: false, loadingActionStats: false })
            }
        }
    }

    private async getNotificationGenStats(): Promise<void> {
        const { notification, domain } = this.state
        const actions = notification?.template?.channels?.default?.actions ?? []
        const hasActions = actions.length > 0

        if (!this.unmounting) {
            await this.setState({ loadingGenStats: true })

            const insightsCalls: Promise<any>[] = []
            insightsCalls.push(
                this.insightsService.fetch(
                    {
                        entity: ReportEntity.NOTIFICATIONS,
                        date_preset: ReportDatePreset.LIFETIME,
                        date_increment: ReportDateIncrement.LIFETIME,
                        breakdowns: ['notification'],
                        fields: [
                            'notification_id',
                            'deliveries',
                            'impressions',
                            'clicks',
                            'primary_clicks',
                            'delivery_rate_decimal',
                            'ctr_decimal',
                            'purchases',
                            'purchase_amount',
                        ],
                        filters: [
                            { field: 'domain.id', operator: 'eq', value: domain.id },
                            { field: 'notification.id', operator: 'eq', value: notification!.id },
                        ],
                    },
                    {
                        cancellationKey: 'notifDetails.genStats',
                    },
                ),
            )

            if (hasActions) {
                insightsCalls.push(
                    this.insightsService.fetch(
                        {
                            entity: ReportEntity.NOTIFICATIONS,
                            date_preset: ReportDatePreset.LIFETIME,
                            date_increment: ReportDateIncrement.LIFETIME,
                            action_attribution: 'event_time',
                            breakdowns: ['notification', 'button'],
                            fields: ['button.id', 'clicks', 'ctr_decimal'],
                            filters: [
                                { field: 'domain.id', operator: 'eq', value: domain.id },
                                { field: 'notification.id', operator: 'eq', value: notification!.id },
                            ],
                        },
                        {
                            cancellationKey: 'notifButtonDetails.bdStats',
                        },
                    ),
                )
            }

            const results = await Promise.all(insightsCalls)

            if (!this.unmounting) {
                this.setState({
                    loadingGenStats: false,
                    loadingActionStats: hasActions,
                    stats: results[0]?.[0],
                })

                if (hasActions) {
                    this.setState({
                        loadingActionStats: false,
                        actionStats: results[1],
                    })
                } else {
                    this.setState({
                        loadingActionStats: false,
                    })
                }
            }
        }
    }

    private refreshStats = async (): Promise<void> => {
        this.setState({ loadingBreakdownStats: true, loadingGenStats: true })
        this.getNotificationGenStats()
        this.getNotificationBreakdownStats()
    }

    private async getNotificationBreakdownStats(): Promise<void> {
        const { notification, domain, statsType } = this.state
        await this.setState({ loadingBreakdownStats: true })
        const contract: InsightsReportContract = {
            entity: ReportEntity.NOTIFICATIONS,
            date_preset: ReportDatePreset.LIFETIME,
            date_increment: statsType,
            action_attribution: 'event_time',
            breakdowns: ['notification', 'channel'],
            fields: [
                'notification_id',
                'deliveries',
                'impressions',
                'clicks',
                'delivery_rate_decimal',
                'ctr_decimal',
                'channel',
            ],
            filters: [
                { field: 'domain.id', operator: 'eq', value: domain.id },
                { field: 'notification.id', operator: 'eq', value: notification!.id },
                { field: 'channel', operator: 'in', value: notification?.channels },
            ],
        }

        const breakdownStats = await this.insightsService.fetch(contract, {
            cancellationKey: 'notifDetails.bdStats',
        })

        if (!breakdownStats?.length || notification === undefined) {
            return this.setState({
                loadingBreakdownStats: false,
            })
        }

        const firstStatDate = moment.tz(breakdownStats[0].event_date, domain.timezone)
        const notifSendDate = moment.tz(moment.utc(notification!.delivery_spec?.send_date_utc), domain.timezone)
        // when notif send date is in STZ the first stats may be from the "previous" day due to tz diffs
        const startDate = firstStatDate.isBefore(notifSendDate) ? firstStatDate : notifSendDate

        let backfilledStats: any[]
        if (statsType === ReportDateIncrement.DAY) {
            backfilledStats = backfillStats(
                breakdownStats,
                statsType,
                {
                    clicks: 0,
                    impressions: 0,
                },
                notification?.channels,
                {
                    startDate,
                    endDate: null,
                },
            )
        } else {
            backfilledStats = breakdownStats
        }

        this.setState({
            loadingBreakdownStats: false,
            breakdownStats: backfilledStats,
            performanceChannel: Array.from(new Set(notification?.channels ?? [])),
        })
    }

    private handleSummaryTitleClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
        this.stopPropagation(ev.nativeEvent)

        if (ev.metaKey) {
            window.open(this.buildScheduleDetailsUrl())
            return
        }

        this.appService.route(this.buildScheduleDetailsUrl())
    }

    private buildScheduleDetailsUrl(): string {
        const notification = this.state.notification
        if (!notification) {
            return this.appService.routeWithin('domain', `/notifications`, true)
        }

        return this.appService.routeWithin('domain', `/notifications/${notification.id}`, true)
    }

    private buildSegmentUrl(segment: GetSegmentResponse): string {
        return this.appService.routeWithinDomain(`/segments/${segment.id}/summary`, true)
    }

    private jumpToSegment = (segment: GetSegmentResponse, ev?: React.MouseEvent): void => {
        if (!ev?.metaKey) {
            this.stopPropagation(ev!.nativeEvent)
            this.appService.route(this.buildSegmentUrl(segment))
        }
    }

    private stopPropagation(ev: Event): void {
        ev.preventDefault()
        ev.stopPropagation()
    }
}
